import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from './index';
import {
  centralLogout,
  setBootWorkspaceClosed,
  updateCentralAccessToken,
} from './slices/centralAuthSlice';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Central session perimeter (refresh / select-workspace / me).
// BeHeard does NOT host these endpoints — historically a refresh proxy
// at /api/auth/central/refresh was assumed to exist on the local
// backend, but it doesn't. We keep the path computation for any future
// proxy deployment but probe-and-disable the refresh attempt at runtime
// to avoid a 404-spam cascade on token expiry.
const CENTRAL_SESSION_URL = API_URL.replace(/\/api\/v\d+$/, '/api/auth/central');

/**
 * Base fetch query with Central access token.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.centralAuth?.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * Separate base query for Central refresh calls (cookie-bearing).
 * Uses CENTRAL_SESSION_URL as base so URLs aren't double-prefixed.
 */
const refreshBaseQuery = fetchBaseQuery({
  baseUrl: CENTRAL_SESSION_URL,
  credentials: 'include', // Send httpOnly cookie (Central session perimeter only)
});

// ──────────────────────────────────────────────────────────────
// Refresh state machine
// ──────────────────────────────────────────────────────────────
//
// In OIDC mode BeHeard does NOT operate a refresh proxy on its own
// backend — refresh either succeeds (cookie path exists) or the
// endpoint returns 404 (no proxy deployed) / 401 (cookie missing).
// Once a refresh attempt fails for either reason, every subsequent
// 401 in the same session would re-attempt the refresh, producing a
// cascade of 404 / 401 noise in the network tab and never recovering.
//
// State machine:
//   - refreshPromise     : single-flight lock for an in-flight attempt
//   - refreshUnavailable : sticky bit set after ANY failed attempt;
//                          subsequent 401s skip refresh entirely.
//
// When refresh is unavailable AND a 401 arrives:
//   1. We dispatch centralLogout() ONCE to clear isAuthenticated.
//   2. AuthGuard observes !isAuthenticated and redirects to /login.
//   3. The OIDC PKCE flow re-authenticates the user.
//
// Workspace-switch races are still protected by the explicit
// isSwitchingWorkspace guard — we do not clear auth during a switch.
// ──────────────────────────────────────────────────────────────

let refreshPromise: Promise<boolean> | null = null;
let refreshUnavailable = false;
let logoutDispatched = false;

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  // Exact-match WORKSPACE_CLOSED interception.
  // Central emits this code with either 409 or 400 depending on the endpoint,
  // so both statuses must route to the workspace-closed UI. Neither may ever
  // fall through to the 401 path (a closed workspace is not an auth failure).
  if (result.error?.status === 409 || result.error?.status === 400) {
    const data = result.error.data as Record<string, unknown> | undefined;
    if (data && data.code === 'WORKSPACE_CLOSED') {
      const otherWorkspaces = Array.isArray(data.otherWorkspaces) ? data.otherWorkspaces : [];
      api.dispatch(setBootWorkspaceClosed({ otherWorkspaces }));
      return result;
    }
  }

  if (result.error?.status !== 401) {
    return result;
  }

  const state = api.getState() as RootState;

  // Only attempt refresh for Central-authenticated users
  if (!state.centralAuth?.isAuthenticated) {
    return result;
  }

  // Never attempt refresh / clear auth during a workspace switch —
  // the switch flow manages its own tokens and a stale 401 from a
  // background re-fetch must not race the switch into a logout.
  if (state.beheardAuthExt?.isSwitchingWorkspace) {
    return result;
  }

  // Sticky-disabled refresh path — we already proved it doesn't work
  // this session. Treat every subsequent 401 as session-expired,
  // dispatch logout once, and let AuthGuard redirect to /login.
  if (refreshUnavailable) {
    if (!logoutDispatched) {
      logoutDispatched = true;
      api.dispatch(centralLogout());
    }
    return result;
  }

  // Single-flight: if a refresh is already in progress, wait for it
  if (refreshPromise) {
    const refreshed = await refreshPromise;
    if (refreshed) {
      return rawBaseQuery(args, api, extraOptions);
    }
    return result;
  }

  // Start refresh
  refreshPromise = (async (): Promise<boolean> => {
    try {
      const refreshResult = await refreshBaseQuery(
        { url: '/refresh', method: 'POST' },
        api,
        extraOptions,
      );

      if (refreshResult.data) {
        const data = refreshResult.data as { accessToken: string; expiresIn: number };
        api.dispatch(
          updateCentralAccessToken({
            accessToken: data.accessToken,
            expiresIn: data.expiresIn,
          }),
        );
        // A successful refresh proves the path works — keep refresh
        // available for next time and reset the logout latch.
        logoutDispatched = false;
        return true;
      }

      // Refresh failed (404 = endpoint missing on this deployment, or
      // 401 = no cookie). Mark the path unavailable so we don't keep
      // pounding it for every queued query.
      refreshUnavailable = true;
      return false;
    } catch {
      // Network error reaching refresh endpoint — same disposition.
      refreshUnavailable = true;
      return false;
    }
  })();

  const refreshed = await refreshPromise;
  refreshPromise = null;

  if (refreshed) {
    return rawBaseQuery(args, api, extraOptions);
  }

  // Refresh just transitioned to unavailable. Dispatch the one-time
  // logout to surface the expired session through AuthGuard.
  if (refreshUnavailable && !logoutDispatched) {
    logoutDispatched = true;
    api.dispatch(centralLogout());
  }

  return result;
};

/**
 * Main BeHeard API.
 *
 * Uses Central access token. On 401:
 *   1. One refresh attempt is made (single-flight).
 *   2. If refresh is missing/failing, the path is marked unavailable
 *      and a single centralLogout() dispatch sends the user through
 *      AuthGuard → /login → OIDC PKCE.
 *   3. No retry storm. No silent swallow. No 404 spam.
 */
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Scenarios', 'Sessions', 'VoiceAvatars', 'Progress', 'Portfolio', 'Highlights', 'Marketplace', 'HireRequests', 'ListenStats', 'Billing', 'SkillCatalog', 'SkillDomains', 'SkillCategories', 'SkillObjectives', 'SkillProgress', 'Templates', 'Assessments', 'PublicProfile', 'ViewStats', 'Petals', 'Events', 'AdminProfiles', 'AdminReports', 'Usage', 'AdminUsage', 'Blueprints', 'NpcPersonas', 'NpcLevels', 'NpcProgress', 'SavedCandidates', 'Evaluations', 'ContactRequests', 'RecruiterDashboard', 'Organizations', 'OrgMembers', 'TrainingModules', 'TrainingAssignments', 'ContentSets', 'CoachProfile', 'Engagements', 'Bookings', 'SharedSessions', 'OrgAnalytics', 'CoachAnalytics', 'Timeline', 'Referrals', 'LearningModules', 'MarketplacePublish', 'Capabilities', 'Endorsements', 'ShareLinks', 'Discovery', 'ScenarioProgression', 'Rubrics'],
  endpoints: () => ({}),
});
